/**
 * SummaryBar — live counts of supported / unsupported / contradicted claims.
 * Click a count to filter the claim list; click again to show all.
 */

import React from 'react';
import {
  CheckmarkFilled,
  CloseFilled,
  Warning,
} from '@e14c/feathers/icons';
import type { ValidationSummary, Verdict } from '../types';

interface SummaryBarProps {
  summary: ValidationSummary;
  totalClaims: number;
  isValidating: boolean;
  activeFilter?: Verdict | null;
  onVerdictFilter?: (verdict: Verdict) => void;
}

const VERDICT_COLUMNS: Array<{
  verdict: Verdict;
  label: string;
  countKey: keyof Pick<
    ValidationSummary,
    'supported' | 'unsupported' | 'contradicted'
  >;
  icon: React.ReactNode;
  valueClass: string;
  activeRingClass: string;
}> = [
  {
    verdict: 'supported',
    label: 'Supported',
    countKey: 'supported',
    icon: <CheckmarkFilled size={20} className="text-highlight-success" />,
    valueClass: 'text-highlight-success',
    activeRingClass: 'ring-highlight-success/40',
  },
  {
    verdict: 'unsupported',
    label: 'Unsupported',
    countKey: 'unsupported',
    icon: <Warning size={20} className="text-highlight-warning" />,
    valueClass: 'text-highlight-warning',
    activeRingClass: 'ring-highlight-warning/40',
  },
  {
    verdict: 'contradicted',
    label: 'Contradicted',
    countKey: 'contradicted',
    icon: <CloseFilled size={20} className="text-highlight-error" />,
    valueClass: 'text-highlight-error',
    activeRingClass: 'ring-highlight-error/40',
  },
];

export const SummaryBar: React.FC<SummaryBarProps> = ({
  summary,
  totalClaims,
  isValidating,
  activeFilter = null,
  onVerdictFilter,
}) => {
  const done = summary.supported + summary.unsupported + summary.contradicted;

  return (
    <div className="bg-muted rounded-md px-4 py-2 grid grid-cols-3 gap-2">
      {VERDICT_COLUMNS.map((column) => {
        const isActive = activeFilter === column.verdict;
        const count = summary[column.countKey];
        const interactive = onVerdictFilter !== undefined;

        const inner = (
          <>
            <div className="flex items-center gap-1">
              {column.icon}
              <span
                className={`text-lg font-semibold tabular-nums ${column.valueClass}`}
              >
                {count}
              </span>
            </div>
            <span className="text-xs text-muted-foreground text-center">
              {column.label}
            </span>
          </>
        );

        if (!interactive) {
          return (
            <div
              key={column.verdict}
              className="flex flex-col items-center gap-0.5"
            >
              {inner}
            </div>
          );
        }

        return (
          <button
            key={column.verdict}
            type="button"
            onClick={() => onVerdictFilter(column.verdict)}
            className={`flex flex-col items-center gap-0.5 rounded-md px-2 py-1 transition-colors cursor-pointer hover:bg-background/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
              isActive
                ? `bg-background/80 ring-2 ring-offset-1 ring-offset-muted ${column.activeRingClass}`
                : ''
            }`}
            aria-pressed={isActive}
            aria-label={`${isActive ? 'Clear filter for' : 'Show only'} ${column.label.toLowerCase()} claims`}
          >
            {inner}
          </button>
        );
      })}

      {isValidating && totalClaims > 0 && (
        <div className="col-span-3 flex items-center justify-center gap-2 mt-0.5">
          <span className="inline-block size-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          <span className="text-xs text-muted-foreground">
            {done}/{totalClaims} validated
          </span>
        </div>
      )}
    </div>
  );
};
